import React from 'react';
import Modal from 'react-modal';
import moment from 'moment';
import type { TTimesheet } from '../../../types/timesheet';

type TimesheetPayRatesModalProps = {
    timesheet: TTimesheet;
    onClose: () => void;
};

const formatDuration = (duration: string) => {
    if (!duration) {
        return '-';
    }

    const parsed = moment.duration(duration);
    const isInvalid = Number.isNaN(parsed.asMinutes());

    if (isInvalid) {
        return duration;
    }

    const hours = parsed.asHours();
    return `${hours.toFixed(2)}h`;
};

const formatClientRate = (rate: string) => {
    const numericRate = parseFloat(rate);

    if (Number.isNaN(numericRate)) {
        return rate || '-';
    }

    return `£${numericRate.toFixed(2)}`;
};

const formatTotal = (duration: string, rate: string) => {
    const numericRate = parseFloat(rate);
    const parsed = moment.duration(duration);
    const isInvalidDuration = Number.isNaN(parsed.asMinutes());
    const isInvalidRate = Number.isNaN(numericRate);

    if (isInvalidDuration || isInvalidRate) {
        return '-';
    }

    const hours = parsed.asHours();
    const total = hours * numericRate;
    return `£${total.toFixed(2)}`;
};

const TimesheetPayRatesModal: React.FC<TimesheetPayRatesModalProps> = ({ timesheet, onClose }) => {
    const payRates = timesheet.pay_rates || [];
    const weekEnding = moment(timesheet.timesheet_date).endOf('isoWeek').format('DD/MM/YYYY');

    return (
        <Modal
            className="pay-rates-modal"
            isOpen={true}
            onRequestClose={onClose}
            contentLabel={`Pay rates for timesheet ${timesheet.timesheet_number}`}
            ariaHideApp={false}
        >
            <div className="modal-header">
                <h2>Pay rates for Timesheet {timesheet.timesheet_number}</h2>
                <button
                    type="button"
                    className="close-button"
                    onClick={onClose}
                    aria-label="Close pay rates modal"
                >
                    ×
                </button>
            </div>

            <div className="modal-body">
                <div className="timesheet-info">
                    <p>
                        <strong>Worker:</strong> {timesheet.freelancer_name}
                    </p>
                    <p>
                        <strong>Week ending:</strong> {weekEnding}
                    </p>
                </div>

                {payRates.length === 0 ? (
                    <p className="pay-rates-modal__empty">
                        No pay rates provided for this timesheet.
                    </p>
                ) : (
                    <div className="pay-rates-modal__table-wrapper">
                        <table className="pay-rates-modal__table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Hours</th>
                                    <th>Charge Rate</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payRates.map(rate => (
                                    <tr key={rate.id}>
                                        <td>
                                            {rate.description || rate.rate_code}
                                        </td>
                                        <td>{formatDuration(rate.duration)}</td>
                                        <td>
                                            {formatClientRate(
                                                rate.client_pay_per_hour,
                                            )}
                                        </td>
                                    <td>
                                        {formatTotal(
                                            rate.duration,
                                            rate.client_pay_per_hour,
                                        )}
                                    </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="modal-bottom-bar">
                <button
                    type="button"
                    className="table-action-button table-action-button-secondary"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default TimesheetPayRatesModal;
